import { useCallback, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MagnifyingGlass } from "react-loader-spinner";
import { callApi, HttpMethod } from "src/services/apiService";
import styles from "./InvestigatePhoneNumber.module.css";
import UserRoleIcon from "src/components/UserRoleIcon";

type HistoryInfo = {
  campaignId: number;
  category: string;
  date: string;
  projId: string;
  roleId: number;
  sessionId: number;
  text: string;
  user: string;
};
type PhoneInfo = {
  city: string;
  country: string;
  initialCarrier: string;
  prefix: string;
  state: string;
  timezone: string;
};

const PAGE_SIZE = 25;

const InvestigatePhoneNumber = () => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm();

  const [phoneNumber, setPhoneNumber] = useState("");
  // Every request carries a token; a response whose token is stale is discarded. A search and a
  // load-more can be in flight together, and without this the slower one appends another
  // number's page onto the timeline and overwrites its cursor.
  const requestToken = useRef(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");
  // Set only when the account holds no reassigned check for this number, which the timeline
  // alone cannot say: an absent check and a check that came back "not reassigned" both leave
  // it empty of reassigned rows.
  const [reassignedMessage, setReassignedMessage] = useState<string>("");
  const [responsePhoneInfo, setResponsePhoneInfo] = useState<PhoneInfo | null>(
    null,
  );
  const [history, setHistory] = useState<HistoryInfo[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  // null until the first response tells us what the categories are; after that the array is
  // literal, so [] means none are ticked rather than "no choice made".
  const [selected, setSelected] = useState<string[] | null>(null);

  // Just the timeline. Changing the filter changes which events are shown, not which number is
  // shown, so the prefix details and the reassigned note stay: both describe the number itself
  // and the server works them out independently of the categories asked for.
  const clearTimeline = useCallback(() => {
    setHistory([]);
    setNextCursor(null);
  }, []);

  // Everything a previous number left behind, the timeline included. Called on the way into every
  // path that changes which number is on screen, rather than on the way out of a successful one:
  // anything left set is shown under the new number, so a failed request or a submit that never
  // reached the API would otherwise label one number's compliance record with another's.
  const clearResults = useCallback(() => {
    clearTimeline();
    setResponsePhoneInfo(null);
    setResponseMessage("");
    setReassignedMessage("");
  }, [clearTimeline]);

  // No acctId: the endpoint resolves the account from the session.
  const fetchPage = useCallback(
    async (digits: string, categories: string[], cursor: string | null) => {
      const params = new URLSearchParams({
        phone: digits,
        take: String(PAGE_SIZE),
      });
      if (categories.length > 0) params.set("categories", categories.join(","));
      if (cursor) params.set("cursor", cursor);

      return await callApi(`main/NumberHistory?${params}`, HttpMethod.GET);
    },
    [],
  );

  const formatPhoneNumber = useCallback((value: any) => {
    if (!value) return value;
    const onlyNums = value.replace(/[^\d]/g, "");
    if (onlyNums.length <= 3) return `(${onlyNums}`;
    if (onlyNums.length <= 6)
      return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3)}`;
    return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(
      6,
      10,
    )}`;
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    if (!dateString || isNaN(Date.parse(dateString + "Z"))) {
      return "";
    }

    var timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcDate = new Date(dateString + "Z");

    return new Intl.DateTimeFormat("en-US", {
      timeZone, // Browser time zone
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    }).format(utcDate);
  }, []);

  /// Runs a search from the top: a new number, or the same number under a different filter.
  const search = useCallback(
    async (formatted: string, categories: string[] | null) => {
      const token = ++requestToken.current;
      setIsLoading(true);
      setHasSearched(true);
      setPhoneNumber(formatted);
      setSelected(categories);

      clearResults();

      try {
        const response = await fetchPage(
          formatted.replace(/[^\d]/g, ""),
          categories ?? [],
          null,
        );

        if (token !== requestToken.current) return;

        setResponseMessage(response?.message ?? "");
        setResponsePhoneInfo(response?.phoneInfo ?? null);
        setHistory(response?.history ?? []);
        setNextCursor(response?.nextCursor ?? null);
        setReassignedMessage(response?.reassignedMessage ?? "");
        if (response?.categories) {
          setAllCategories(response.categories);
          // The first search names no categories, so adopt the full set the server reports.
          if (categories === null) setSelected(response.categories);
        }
      } catch (error) {
        console.error("Error fetching Number History", error);
        if (token === requestToken.current) {
          setResponseMessage(
            "Could not load history for this number. Please try again.",
          );
        }
      } finally {
        if (token === requestToken.current) setIsLoading(false);
      }
    },
    [clearResults, fetchPage],
  );

  const loadMore = useCallback(async () => {
    // isLoadingMore also guards a double click, which would otherwise refetch the same cursor
    // and append the same rows twice.
    if (!nextCursor || isLoadingMore || isLoading) return;
    const token = ++requestToken.current;
    setIsLoadingMore(true);
    try {
      const response = await fetchPage(
        phoneNumber.replace(/[^\d]/g, ""),
        selected ?? [],
        nextCursor,
      );
      if (token !== requestToken.current) return;

      setHistory((current) => [...current, ...(response?.history ?? [])]);
      setNextCursor(response?.nextCursor ?? null);
    } catch (error) {
      console.error("Error fetching more Number History", error);
    } finally {
      if (token === requestToken.current) setIsLoadingMore(false);
    }
  }, [fetchPage, isLoading, isLoadingMore, nextCursor, phoneNumber, selected]);

  const onSubmit = useCallback(
    async (data: any) => {
      // An empty list means "everything" to the API, so submitting with nothing ticked would
      // quietly return every category. Hold the empty state here the way toggling does.
      if (selected !== null && selected.length === 0) {
        setPhoneNumber(data.phoneNumber);
        setHasSearched(true);
        clearResults();
        return;
      }
      await search(data.phoneNumber, selected);
    },
    [clearResults, search, selected],
  );

  // Changing the filter re-runs the search rather than hiding rows already on screen: the server
  // holds far more history than one page of it, so filtering here would only ever search the
  // first 25 events.
  const applyFilter = useCallback(
    (next: string[]) => {
      // An empty request means "everything" to the server, which would be the opposite of what
      // unticking the last box asks for. Hold the empty state in the UI and prompt instead.
      if (next.length === 0) {
        setSelected([]);
        clearTimeline();
        return;
      }
      search(phoneNumber, next);
    },
    [clearTimeline, phoneNumber, search],
  );

  const ticked = selected ?? allCategories;
  const isSelected = (category: string) => ticked.includes(category);

  const toggleCategory = (category: string) =>
    applyFilter(
      isSelected(category)
        ? ticked.filter((c) => c !== category)
        : [...ticked, category],
    );

  return (
    <div className="container-fluid">
      <h1>Investigate Phone Number</h1>
      <div className="card mt-3">
        <div className="card-header">Investigate Phone Number</div>
        <div className="card-body">
          <p>
            This form checks the history of a single phone number in your
            account. Its primary purpose is to aid in DNC violation
            investigations. Note that this history does not provide information
            about what exemptions and settings may have been in place at the
            time of scrubbing. The most recent events are shown first.
          </p>

          <p>
            We frequently update the DNC databases in our system. It is
            important to note that the date shown for when a phone number was
            added to a particular database is the date this number was part of a
            database update. It is NOT indicative of the true date that the
            consumer added themselves to the original database, which is not
            provided to us by the database supplier.
          </p>

          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-3">
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Phone Number is required",
                    pattern: {
                      value: /^\(\d{3}\) \d{3}-\d{4}$/,
                      message:
                        "Phone Number must be 10 digits number and in (xxx) xxx-xxxx format",
                    },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      onChange={(e) =>
                        field.onChange(formatPhoneNumber(e.target.value))
                      }
                      className={`form-control ${
                        errors.phoneNumber ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Phone Number"
                      type="text"
                    />
                  )}
                />
                {errors.phoneNumber && (
                  <p>{errors.phoneNumber.message?.toString()}</p>
                )}
              </div>

              <button
                disabled={!isDirty || isLoading || isLoadingMore}
                type="submit"
                className="btn btn-primary"
              >
                Get History
              </button>
            </form>
          </div>
          {isLoading && (
            <div className="loader-container">
              <MagnifyingGlass
                visible={isLoading}
                height="80"
                width="80"
                ariaLabel="MagnifyingGlass-loading"
                wrapperStyle={{}}
                wrapperClass="MagnifyingGlass-wrapper"
                glassColor="#c0efff"
                color="#0d6efd"
              />
            </div>
          )}
          {responseMessage && !isLoading && (
            <div style={{ marginTop: 20 }}>
              <span>
                {responseMessage}
                <b style={{ marginLeft: 5 }}>{phoneNumber}</b>
              </span>
            </div>
          )}
        </div>
      </div>
      {responsePhoneInfo && !isLoading ? (
        <div className="card mt-3">
          <div className="card-header">
            {" "}
            Information for Area Code and Prefix
          </div>
          <div className="card-body">
            <table className="table table-bordered table-hover table-striped border-primary">
              <thead>
                <tr>
                  <th className="text-center">City</th>
                  <th className="text-center">State</th>
                  <th className="text-center">Country</th>
                  <th className="text-center">Timezone</th>
                  <th className="text-center">Initial Carrier</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{responsePhoneInfo.city}</td>
                  <td>{responsePhoneInfo.state}</td>
                  <td>{responsePhoneInfo.country}</td>
                  <td>{responsePhoneInfo.timezone}</td>
                  <td>{responsePhoneInfo.initialCarrier}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {responsePhoneInfo?.prefix && !isLoading ? (
        <div className="card mt-3">
          <div className="card-header"> Prefix Databases</div>
          <div className="card-body">
            <b>{responsePhoneInfo.prefix}</b>
          </div>
        </div>
      ) : null}
      {!isLoading && hasSearched && !responseMessage ? (
        <div className="card mt-3">
          <div className="card-header">
            Scrub history for phone # <b>{phoneNumber}</b>, most recent first
          </div>
          <div className="card-body">
            {allCategories.length > 0 && (
              <div className="mb-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <span className="fw-semibold">Show events:</span>
                  {allCategories.map((category) => (
                    <div className="form-check mb-0" key={category}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`category-${category}`}
                        checked={isSelected(category)}
                        disabled={isLoading || isLoadingMore}
                        onChange={() => toggleCategory(category)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`category-${category}`}
                      >
                        {category}
                      </label>
                      {/* One click for the common case — "just show me the reassigned checks" —
                          instead of clearing everything and ticking one box back on. */}
                      <button
                        type="button"
                        className="btn btn-link btn-sm py-0 px-1 align-baseline"
                        disabled={isLoading || isLoadingMore}
                        aria-label={`Show only ${category}`}
                        onClick={() => applyFilter([category])}
                      >
                        only
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    disabled={isLoading || isLoadingMore}
                    onClick={() => applyFilter(allCategories)}
                  >
                    Select all
                  </button>
                </div>
              </div>
            )}

            {history.length > 0 ? (
              <table className="table table-bordered table-hover table-striped border-primary">
                <thead className={`${styles["sticky-header"]}`}>
                  <tr>
                    <th className="text-center">Event</th>
                    <th className="text-center">Date</th>
                    <th className="text-center">Project</th>
                    <th className="text-center">By User</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item: HistoryInfo, index: number) => (
                    <tr key={index}>
                      <td>{item.text}</td>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.projId}</td>
                      <td style={{ backgroundColor: "inherit" }}>
                        {item.user && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            <UserRoleIcon Role={item.roleId} />
                            <span>{item.user}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : ticked.length === 0 ? (
              <span>
                Tick an event type above to see this number's history.
              </span>
            ) : (
              <span>No history of this type found for this number.</span>
            )}

            <div className="d-flex align-items-center gap-3">
              {nextCursor && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={isLoadingMore}
                  onClick={loadMore}
                >
                  {isLoadingMore ? "Loading…" : `Load ${PAGE_SIZE} more`}
                </button>
              )}
              {history.length > 0 && (
                <span className="text-muted">
                  Showing {history.length}
                  {nextCursor ? " so far" : " (end of history)"}
                </span>
              )}
            </div>

            {reassignedMessage && (
              <p className="mt-3 mb-0">{reassignedMessage}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InvestigatePhoneNumber;
